/**
 * Size Chart Recommender — Native size chart with auto-detect & recommender
 *
 * Custom element that loads product type data from a JSON config,
 * auto-detects the product type from the title, and renders a
 * modern single-view size chart with a built-in size recommender.
 */

if (!customElements.get('size-chart-recommender')) {
  class SizeChartRecommender extends HTMLElement {
    static _configCache = null;
    static _configPromise = null;

    connectedCallback() {
      this._title = this.getAttribute('product-title') || '';
      this._configUrl = this.getAttribute('config-url');
      try { this._unit = localStorage.getItem('ll-size-unit') || 'cm'; } catch (e) { this._unit = 'cm'; }

      if (!this._configUrl) return;
      this._init();
    }

    async _init() {
      try {
        const config = await this._fetchConfig();
        this._type = this._detectType(config);

        if (!this._type) {
          this._renderNoMatch();
          return;
        }

        this._render();
      } catch (err) {
        /* fail silently */
      }
    }

    /* --------------------------------------------------------------- */
    /*  Data                                                            */
    /* --------------------------------------------------------------- */

    async _fetchConfig() {
      if (SizeChartRecommender._configCache) {
        return SizeChartRecommender._configCache;
      }
      if (!SizeChartRecommender._configPromise) {
        SizeChartRecommender._configPromise = fetch(this._configUrl)
          .then(r => r.json())
          .then(data => {
            SizeChartRecommender._configCache = data;
            return data;
          });
      }
      return SizeChartRecommender._configPromise;
    }

    _detectType(config) {
      if (!config || !config.product_types) return null;
      var titleLower = this._title.toLowerCase();

      /* Sort patterns longest-first to prevent partial matches */
      var sorted = config.product_types.slice().sort((a, b) => {
        var aLen = Math.max(...a.title_patterns.map(p => p.length));
        var bLen = Math.max(...b.title_patterns.map(p => p.length));
        return bLen - aLen;
      });

      for (var i = 0; i < sorted.length; i++) {
        var type = sorted[i];
        for (var j = 0; j < type.title_patterns.length; j++) {
          if (titleLower.indexOf(type.title_patterns[j].toLowerCase()) !== -1) {
            return type;
          }
        }
      }
      return null;
    }

    /* --------------------------------------------------------------- */
    /*  Rendering                                                       */
    /* --------------------------------------------------------------- */

    _render() {
      var type = this._type;
      var html = '<div class="size-chart">';

      /* ---- Section 1: Size Chart ---- */
      html += '<section class="sc-section">';
      html += '<div class="sc-section__header">';
      html += '<h3 class="sc-section__title">';
      html += '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="5" width="16" height="8" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M4 5v3M7 5v2M10 5v3M13 5v2M16 5v1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
      html += ' Size Chart</h3>';
      html += this._renderUnitToggle();
      html += '</div>';

      /* Lead note sits ABOVE the table on purpose. On the headguards the single
         biggest cause of a wrong size is someone sizing by age, or by the label
         printed on another brand's headgear, without ever reaching for a tape.
         Saying so before they read the numbers is the point. */
      if (type.lead_note) {
        html += '<p class="sc-lead">' + this._esc(type.lead_note) + '</p>';
      }

      html += this._renderTable(type);

      if (type.fit_note) {
        html += '<div class="sc-note">' +
          '<svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M8 7v4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="4.75" r="0.75" fill="currentColor"/></svg>' +
          '<span>' + this._esc(type.fit_note) + '</span></div>';
      }

      /* How to fit - ordered steps. Matters most on the headguards, where the
         back lace has to be fully opened before the thing will go on at all. */
      if (type.how_to_fit && type.how_to_fit.length) {
        html += '<div class="sc-howto">';
        if (type.how_to_fit_title) {
          html += '<p class="sc-howto__title">' + this._esc(type.how_to_fit_title) + '</p>';
        }
        /* Real numeral elements rather than ::marker, so the number can be a
           proper counter chip - sized, centred and aligned to the first line of
           text. ::marker cannot be given a box in any engine we support.
           The <ol> still carries the semantics, and the chip is aria-hidden so
           a screen reader hears the list numbering once, not twice. */
        html += '<ol class="sc-howto__list">';
        for (var f = 0; f < type.how_to_fit.length; f++) {
          html += '<li class="sc-howto__step">' +
            '<span class="sc-howto__num" aria-hidden="true">' + (f + 1) + '</span>' +
            '<span class="sc-howto__text">' + this._esc(type.how_to_fit[f]) + '</span>' +
            '</li>';
        }
        html += '</ol></div>';
      }

      /* Fit dimensions a head measurement cannot capture: how deep it sits on
         the forehead, and chin strap length. Both have produced real returns
         where the circumference itself was correct, so they get said out loud
         rather than left for the customer to discover. */
      if (type.extra_notes && type.extra_notes.length) {
        html += '<div class="sc-extra">';
        if (type.extra_notes_title) {
          html += '<p class="sc-extra__title">' + this._esc(type.extra_notes_title) + '</p>';
        }
        html += '<ul class="sc-extra__list">';
        for (var n = 0; n < type.extra_notes.length; n++) {
          html += '<li>' + this._esc(type.extra_notes[n]) + '</li>';
        }
        html += '</ul></div>';
      }

      html += '</section>';

      /* ---- Section 2: Find My Size ---- */
      if (type.recommender) {
        html += '<section class="sc-section sc-section--recommend">';
        html += '<h3 class="sc-section__title">';
        html += '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7.25" stroke="currentColor" stroke-width="1.4"/><path d="M9 5.5v4l2.5 1.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        html += ' Find Your Size</h3>';

        /* Measurement guide */
        html += '<div class="sc-guide">' +
          '<svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M3 17L17 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M7 13l-1-1M9 11l-1-1M11 9l-1-1M13 7l-1-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '<span>' + this._esc(type.measurement_guide) + '</span></div>';

        /* The no-tape path is load-bearing, not a nicety. A real customer told
           us "I will find a tape measure tomorrow" and the swap stalled there. */
        if (type.no_tape_hint) {
          html += '<p class="sc-notape">' + this._esc(type.no_tape_hint) + '</p>';
        }

        /* Input */
        var rec = type.recommender;
        var unitLabel = this._unit === 'cm' ? 'cm' : 'in';
        var placeholder = this._unit === 'cm' ? 'e.g. 58' : 'e.g. 22.8';
        if (type.measurement_type === 'body') {
          placeholder = this._unit === 'cm' ? 'e.g. 104' : 'e.g. 40.9';
        }

        /* Method toggle — tape measure (exact) vs height + weight (estimate) */
        if (rec.hw_estimate) {
          var tapeOn = this._recMode !== 'hw';
          html += '<div class="sc-mode">';
          html += '<button type="button" class="sc-mode-btn" data-rec-mode="tape" aria-pressed="' + tapeOn + '">Tape measure</button>';
          html += '<button type="button" class="sc-mode-btn" data-rec-mode="hw" aria-pressed="' + !tapeOn + '">No tape? Height + weight</button>';
          html += '</div>';
        }

        html += '<div class="sc-input-group" data-rec-pane="tape"' + (rec.hw_estimate && this._recMode === 'hw' ? ' hidden' : '') + '>';
        html += '<label class="sc-input-label">' + this._esc(rec.input_label) + '</label>';
        html += '<div class="sc-input-row">';
        html += '<div class="sc-input-field">';
        html += '<input type="number" step="0.1" min="0" placeholder="' + placeholder + '" data-rec-input autocomplete="off" />';
        html += '<span class="sc-input-unit" data-unit-label>' + unitLabel + '</span>';
        html += '</div>';
        html += '<button class="sc-find-btn" data-rec-btn>Find My Size</button>';
        html += '</div></div>';

        if (rec.hw_estimate) {
          html += '<div class="sc-input-group" data-rec-pane="hw"' + (this._recMode === 'hw' ? '' : ' hidden') + '>';
          html += '<label class="sc-input-label">Your height and weight</label>';
          html += '<div class="sc-input-row">';
          html += '<div class="sc-input-field">';
          html += '<input type="number" step="1" min="0" placeholder="Height, e.g. 178" data-hw-height autocomplete="off" aria-label="Height in centimetres" />';
          html += '<span class="sc-input-unit">cm</span>';
          html += '</div>';
          html += '<div class="sc-input-field">';
          html += '<input type="number" step="1" min="0" placeholder="Weight, e.g. 85" data-hw-weight autocomplete="off" aria-label="Weight in kilograms" />';
          html += '<span class="sc-input-unit">kg</span>';
          html += '</div>';
          html += '<button class="sc-find-btn" data-rec-btn>Find My Size</button>';
          html += '</div></div>';
        }

        /* Result area */
        html += '<div class="sc-result" data-rec-result></div>';
        html += '</section>';
      }

      html += '</div>';

      this.innerHTML = html;
      this._bindEvents();
    }

    _renderUnitToggle() {
      var cmSel = this._unit === 'cm' ? ' aria-pressed="true"' : ' aria-pressed="false"';
      var inSel = this._unit === 'in' ? ' aria-pressed="true"' : ' aria-pressed="false"';
      return '<div class="sc-unit-toggle">' +
        '<button class="sc-unit-btn" data-unit="cm"' + cmSel + '>CM</button>' +
        '<button class="sc-unit-btn" data-unit="in"' + inSel + '>IN</button>' +
        '</div>';
    }

    _renderTable(type) {
      var cols = type.columns;
      var sizes = type.sizes;
      var hasHalf = cols.some(c => c.is_half);

      var html = '<div class="sc-table-wrap"><table class="sc-table"><thead><tr>';
      html += '<th>Size</th>';
      for (var i = 0; i < cols.length; i++) {
        html += '<th>' + this._esc(cols[i].label) + '</th>';
      }
      html += '</tr></thead><tbody>';

      for (var s = 0; s < sizes.length; s++) {
        html += '<tr>';
        html += '<td>' + this._esc(sizes[s].label) + '</td>';
        for (var c = 0; c < cols.length; c++) {
          var key = cols[c].key;
          var rangeKey = key + '_range';
          if (cols[c].text) {
            /* Plain-text column (e.g. "Typical fit"). Deliberately carries no
               data-cm / data-range, so the CM/IN toggle leaves it alone. */
            var txt = sizes[s][key];
            html += '<td class="sc-table__text">' + this._esc(txt == null ? '' : txt) + '</td>';
          } else if (sizes[s][rangeKey]) {
            html += '<td data-range="' + this._esc(sizes[s][rangeKey]) + '">' + this._convertRange(sizes[s][rangeKey]) + '</td>';
          } else {
            var val = sizes[s][key];
            html += '<td data-cm="' + val + '">' + this._convertVal(val) + '</td>';
          }
        }
        html += '</tr>';
      }

      html += '</tbody></table></div>';

      if (hasHalf) {
        html += '<p class="sc-half-note">* Measurements shown as half-width. Double for full circumference.</p>';
      }

      return html;
    }

    _renderNoMatch() {
      /* 21 Oct 2026: Pro Sports Lids / Retro Lids have no chart type. Hide the trigger(s) for this drawer so
         "Find your size" never opens "not available". Washing & care sits beside it and is left alone. */
      try {
        var drawer = this.closest('x-drawer');
        if (drawer && drawer.id) {
          document.querySelectorAll('[aria-controls="' + drawer.id + '"]').forEach(function (b) { b.style.display = 'none'; });
        }
      } catch (e) { /* never block the page */ }
      this.innerHTML = '<div class="size-chart"><div class="sc-empty">' +
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 21L21 3M3 3l18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
        '<p>Size chart not available for this product type.</p>' +
        '</div></div>';
    }

    /* --------------------------------------------------------------- */
    /*  Events                                                          */
    /* --------------------------------------------------------------- */

    _bindEvents() {
      var self = this;

      /* Unit toggle */
      var unitBtns = this.querySelectorAll('.sc-unit-btn');
      unitBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var unit = this.getAttribute('data-unit');
          self._unit = unit;
          try { localStorage.setItem('ll-size-unit', unit); } catch (e) {}

          /* Update all unit buttons */
          self.querySelectorAll('.sc-unit-btn').forEach(function(b) {
            b.setAttribute('aria-pressed', b.getAttribute('data-unit') === unit ? 'true' : 'false');
          });

          /* Update table values */
          self._updateTableUnits();

          /* Update input placeholders and unit labels */
          self._updateInputUnits();
        });
      });

      /* Recommender */
      this.querySelectorAll('[data-rec-btn]').forEach(function(btn) {
        btn.addEventListener('click', function() {
          self._runRecommender();
        });
      });
      this.querySelectorAll('[data-rec-input], [data-hw-height], [data-hw-weight]').forEach(function(input) {
        input.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            self._runRecommender();
          }
        });
      });

      /* Tape / height+weight method toggle */
      this.querySelectorAll('[data-rec-mode]').forEach(function(btn) {
        btn.addEventListener('click', function() {
          var mode = btn.getAttribute('data-rec-mode');
          if (mode === self._recMode) return;
          self._recMode = mode;
          self.querySelectorAll('[data-rec-mode]').forEach(function(b) {
            b.setAttribute('aria-pressed', b.getAttribute('data-rec-mode') === mode ? 'true' : 'false');
          });
          self.querySelectorAll('[data-rec-pane]').forEach(function(pane) {
            pane.hidden = pane.getAttribute('data-rec-pane') !== mode;
          });
          var result = self.querySelector('[data-rec-result]');
          if (result) result.innerHTML = '';
        });
      });
    }

    /* --------------------------------------------------------------- */
    /*  Unit conversion                                                 */
    /* --------------------------------------------------------------- */

    _cmToIn(cm) {
      return Math.round(cm / 2.54 * 10) / 10;
    }

    _inToCm(inches) {
      return Math.round(inches * 2.54 * 10) / 10;
    }

    _convertVal(cm) {
      if (this._unit === 'in') {
        return this._cmToIn(cm) + '"';
      }
      return cm + ' cm';
    }

    _convertRange(rangeStr) {
      var parts = rangeStr.split('–').map(function(s) { return s.trim(); });
      if (parts.length !== 2) return rangeStr;
      var a = parseFloat(parts[0]);
      var b = parseFloat(parts[1]);
      if (isNaN(a) || isNaN(b)) return rangeStr;

      if (this._unit === 'in') {
        return this._cmToIn(a) + '" – ' + this._cmToIn(b) + '"';
      }
      return a + ' – ' + b + ' cm';
    }

    _updateTableUnits() {
      var self = this;
      this.querySelectorAll('.sc-table td[data-cm]').forEach(function(td) {
        var cm = parseFloat(td.getAttribute('data-cm'));
        td.textContent = self._convertVal(cm);
      });
      this.querySelectorAll('.sc-table td[data-range]').forEach(function(td) {
        var range = td.getAttribute('data-range');
        td.textContent = self._convertRange(range);
      });
    }

    _updateInputUnits() {
      var unitLabel = this._unit === 'cm' ? 'cm' : 'in';
      this.querySelectorAll('[data-unit-label]').forEach(function(el) {
        el.textContent = unitLabel;
      });

      var type = this._type;
      var placeholder = this._unit === 'cm' ? 'e.g. 58' : 'e.g. 22.8';
      if (type && type.measurement_type === 'body') {
        placeholder = this._unit === 'cm' ? 'e.g. 104' : 'e.g. 40.9';
      }
      this.querySelectorAll('[data-rec-input]').forEach(function(input) {
        input.placeholder = placeholder;
      });
    }

    /* --------------------------------------------------------------- */
    /*  Recommender logic                                               */
    /* --------------------------------------------------------------- */

    /* Estimate a body measurement from height (cm) + weight (kg).
       Kids under 150cm size by height directly (adult regressions do not
       hold for children); adults + teens use anthropometric regressions
       accurate to roughly one size band, biased safe by the "size up" rule. */
    _estimateFromHw(kind, h, w) {
      if (h < 150) {
        var y;
        if (h < 124) y = 'Youth S';
        else if (h < 135) y = 'Youth M';
        else if (h < 146) y = 'Youth L';
        else y = 'Youth XL';
        return { size: y };
      }
      var est = kind === 'waist'
        ? 45 + 0.5 * w - 0.15 * (h - 175)
        : 57.75 + 0.55 * w - 0.25 * (h - 175);
      return { cm: Math.round(est) };
    }

    _runRecommender() {
      var resultArea = this.querySelector('[data-rec-result]');
      var rec = this._type && this._type.recommender;
      if (!resultArea || !rec || !rec.fit_ranges) return;

      var valCm;
      var opts = {};

      if (this._recMode === 'hw' && rec.hw_estimate) {
        var hEl = this.querySelector('[data-hw-height]');
        var wEl = this.querySelector('[data-hw-weight]');
        var h = parseFloat(hEl && hEl.value);
        var w = parseFloat(wEl && wEl.value);
        if (isNaN(h) || h < 90 || h > 230) {
          resultArea.innerHTML = '';
          if (hEl) hEl.focus();
          return;
        }
        if (isNaN(w) || w < 12 || w > 300) {
          resultArea.innerHTML = '';
          if (wEl) wEl.focus();
          return;
        }
        opts.estimated = true;
        var est = this._estimateFromHw(rec.hw_estimate, h, w);
        if (est.size) {
          this._renderResult(resultArea, { size: est.size }, 'comfortable', null, opts);
          return;
        }
        valCm = est.cm;
      } else {
        var input = this.querySelector('[data-rec-input]');
        if (!input) return;

        var val = parseFloat(input.value);
        if (isNaN(val) || val <= 0) {
          resultArea.innerHTML = '';
          input.focus();
          return;
        }

        /* Convert to cm if input is in inches */
        valCm = this._unit === 'in' ? this._inToCm(val) : val;
      }

      var ranges = rec.fit_ranges;

      /* V2 engine. Opted into by any product type carrying the newer
         recommender fields, so the four older charts keep their exact current
         behaviour. Bands here are contiguous and non-overlapping, and the
         boundary between two sizes is read off the NEXT band's minimum rather
         than the current band's maximum. That closes the hole where 52.5 — or
         any inch conversion landing between two bands — fell through the gap
         and got bumped to the wrong size. */
      if (rec.below_min_message || rec.age_reference) {
        this._renderResultV2(resultArea, rec, ranges, valCm);
        return;
      }

      var bestMatch = null;
      var bestFit = null;

      for (var i = 0; i < ranges.length; i++) {
        var r = ranges[i];
        if (valCm >= r.min_cm && valCm <= r.max_cm) {
          var mid = (r.min_cm + r.max_cm) / 2;
          var rangeSize = r.max_cm - r.min_cm;
          /* Directional: below the band's midpoint = extra room (always
             comfortable); only the top end of a band trends snug/tight. */
          var ratio = (valCm - mid) / (rangeSize / 2);

          if (ratio <= 0.5) {
            bestFit = 'comfortable';
          } else if (ratio <= 0.8) {
            bestFit = 'snug';
          } else {
            bestFit = 'tight';
          }
          bestMatch = r;
          break;
        }
      }

      /* If no exact match, find closest (prefer larger for Polynesian audience) */
      if (!bestMatch) {
        if (valCm < ranges[0].min_cm) {
          /* Smaller than our smallest band: the smallest size runs roomy on
             them, not snug. */
          bestMatch = ranges[0];
          bestFit = 'comfortable';
        } else if (valCm > ranges[ranges.length - 1].max_cm) {
          bestMatch = ranges[ranges.length - 1];
          bestFit = 'tight';
        } else {
          for (var k = 0; k < ranges.length - 1; k++) {
            if (valCm > ranges[k].max_cm && valCm < ranges[k + 1].min_cm) {
              bestMatch = ranges[k + 1];
              bestFit = 'snug';
              break;
            }
          }
        }
      }

      if (!bestMatch) return;

      this._renderResult(resultArea, bestMatch, bestFit, valCm, opts);
    }

    /* --------------------------------------------------------------- */
    /*  Recommender logic — V2                                          */
    /* --------------------------------------------------------------- */

    /* Bands are contiguous and non-overlapping. A band's upper edge is the NEXT
       band's minimum, so no value — integer or decimal — can fall between two
       sizes and get silently bumped. */
    _pickBand(ranges, v) {
      if (v < ranges[0].min_cm) return { kind: 'below' };
      for (var i = 0; i < ranges.length; i++) {
        var upper = (i < ranges.length - 1) ? ranges[i + 1].min_cm : ranges[i].max_cm + 1;
        if (v < upper) {
          return { kind: 'band', band: ranges[i], next: ranges[i + 1] || null };
        }
      }
      return { kind: 'above', band: ranges[ranges.length - 1] };
    }

    /* Age sanity-checks the MEASUREMENT. It never selects the size — sizing by
       age is the most common way customers get this wrong. */
    _ageLabel(rec, v) {
      if (!rec.age_reference) return null;
      for (var i = 0; i < rec.age_reference.length; i++) {
        if (v <= rec.age_reference[i].max_cm) return rec.age_reference[i].label;
      }
      return null;
    }

    _fitIcon(tone) {
      if (tone === 'comfortable') {
        return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      if (tone === 'snug') {
        return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5" stroke="currentColor" stroke-width="2"/></svg>';
      }
      return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2.5v6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="8" cy="12.5" r="1" fill="currentColor"/></svg>';
    }

    _renderResultV2(container, rec, ranges, valCm) {
      var pick = this._pickBand(ranges, valCm);
      var display = this._unit === 'in'
        ? this._cmToIn(valCm) + '"'
        : (Math.round(valCm * 10) / 10) + ' cm';

      var tone = 'comfortable';
      var eyebrow = 'We recommend';
      var heading = '';
      var lines = [];

      if (pick.kind === 'below') {
        /* Never dress an out-of-range measurement up as a comfortable fit. */
        tone = 'tight';
        eyebrow = 'No size for this yet';
        heading = display;
        lines.push(this._esc(rec.below_min_message || ''));
      } else {
        var b = pick.band;
        heading = 'Size ' + b.size;
        lines.push('Based on a measurement of <strong>' + this._esc(display) + '</strong>.');

        if (pick.kind === 'above') {
          /* Largest size we make. Never tell someone to size up into nothing. */
          tone = 'snug';
          lines.push(this._esc(rec.above_max_message || ''));
        } else if (b.status === 'coming_soon') {
          tone = 'snug';
          eyebrow = 'The right size for this is';
          lines.push(this._esc(b.coming_soon_message || ''));
          if (b.interim_message && b.interim_from_cm != null && valCm >= b.interim_from_cm) {
            lines.push(this._esc(b.interim_message));
          }
        } else if (valCm >= b.max_cm - 0.99) {
          /* Top of a band is a normal, correct fit — not a nudge to size up.
             The lace opens a long way, and a bigger size that slides around or
             drops over the eyebrows is worse at the job than a snug one. */
          tone = 'comfortable';
          lines.push('This is the top end of ' + this._esc(b.size) +
            '. The lace will sit most of the way open, which is normal and still a proper fit.');
        } else if (valCm <= b.min_cm + 0.99) {
          lines.push('This is the small end of ' + this._esc(b.size) + '. Lace it in firmly and it will sit right.');
        } else {
          lines.push('Good fit, with lace left to adjust in both directions.');
        }

        var age = this._ageLabel(rec, valCm);
        if (age) {
          lines.push('<span class="sc-result-estimate">For reference, ' + this._esc(display) +
            ' is about average for ' + this._esc(age) +
            '. If this is for someone much older or younger, it is worth checking the tape was level and snug.</span>');
        }
      }

      var toneLabels = { comfortable: 'Good fit', snug: 'Worth reading', tight: 'Heads up' };
      var body = '';
      for (var i = 0; i < lines.length; i++) body += '<p>' + lines[i] + '</p>';

      container.innerHTML =
        '<div class="sc-result-card sc-result-card--' + tone + '">' +
          '<div class="sc-result-top">' +
            '<div class="sc-result-size">' +
              '<span class="sc-result-size__label">' + this._esc(eyebrow) + '</span>' +
              '<span class="sc-result-size__value">' + this._esc(heading) + '</span>' +
            '</div>' +
            '<span class="sc-result-fit sc-result-fit--' + tone + '">' + this._fitIcon(tone) + ' ' + toneLabels[tone] + '</span>' +
          '</div>' +
          '<div class="sc-result-body">' + body + '</div>' +
        '</div>';

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      container.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    }

    _renderResult(container, match, fit, valueCm, opts) {
      opts = opts || {};
      var fitLabels = {
        comfortable: 'Comfortable Fit',
        snug: 'Snug Fit',
        tight: 'Tight Fit'
      };

      var fitIcons = {
        comfortable: '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.5 3.5L13 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        snug: '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5" stroke="currentColor" stroke-width="2"/></svg>',
        tight: '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
      };

      var fitMessages = {
        comfortable: 'This size will give you a <strong>great, comfortable fit</strong> based on your measurement.',
        snug: 'This size will fit well but may feel <strong>slightly snug</strong>. It should loosen with wear.',
        tight: 'This is the closest size available, but it may feel <strong>tight</strong>. Consider sizing up if possible.'
      };

      var basedOn;
      if (opts.estimated) {
        basedOn = 'Based on your height and weight, we recommend <strong>Size ' + this._esc(match.size) + '</strong>.';
      } else {
        var valueDisplay = this._unit === 'in' ? this._cmToIn(valueCm) + '"' : Math.round(valueCm) + ' cm';
        basedOn = 'Based on your measurement of <strong>' + valueDisplay + '</strong>, we recommend <strong>Size ' + this._esc(match.size) + '</strong>.';
      }

      container.innerHTML =
        '<div class="sc-result-card sc-result-card--' + fit + '">' +
          '<div class="sc-result-top">' +
            '<div class="sc-result-size">' +
              '<span class="sc-result-size__label">We recommend</span>' +
              '<span class="sc-result-size__value">Size ' + this._esc(match.size) + '</span>' +
            '</div>' +
            '<span class="sc-result-fit sc-result-fit--' + fit + '">' + fitIcons[fit] + ' ' + fitLabels[fit] + '</span>' +
          '</div>' +
          '<div class="sc-result-body">' +
            '<p>' + basedOn + '</p>' +
            '<p>' + fitMessages[fit] + '</p>' +
            (opts.estimated ? '<p class="sc-result-estimate">This is an estimate. For the most exact match, use the tape measure method.</p>' : '') +
          '</div>' +
        '</div>';

      /* Bring the answer into view — it renders below the button and is
         easy to miss inside the drawer. */
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      container.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    }

    /* --------------------------------------------------------------- */
    /*  Helpers                                                         */
    /* --------------------------------------------------------------- */

    _esc(str) {
      if (!str) return '';
      var d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  }

  customElements.define('size-chart-recommender', SizeChartRecommender);
}
